import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Tooltip,
  Alert,
  Snackbar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  useTheme,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import PreviewIcon from '@mui/icons-material/Preview';
import EmailIcon from '@mui/icons-material/Email';
import HtmlEmailEditor from '../../components/HtmlEmailEditor';
import {
  getAllCampaigns,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  sendCampaign,
  sendTestEmail,
  previewCampaign,
  EmailCampaignData,
  EmailPreview
} from '../../services/emailCampaignService';
import { formatDateTime } from '../../utils/dateUtils';

// Componente para renderizar o status da campanha
const CampaignStatus: React.FC<{ status: string }> = ({ status }) => {
  const statusConfig: Record<string, { color: 'default' | 'info' | 'success' | 'warning' | 'error', label: string }> = {
    draft: { color: 'default', label: 'Rascunho' },
    scheduled: { color: 'info', label: 'Agendada' },
    sending: { color: 'warning', label: 'Enviando' },
    sent: { color: 'success', label: 'Enviada' },
    failed: { color: 'error', label: 'Falhou' }
  };

  const config = statusConfig[status] || statusConfig.draft;
  return <Chip label={config.label} color={config.color} size="small" />;
};

const EmailCampaignPage: React.FC = () => {
  const theme = useTheme();
  const [campaigns, setCampaigns] = useState<EmailCampaignData[]>([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState<EmailCampaignData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<EmailCampaignData | null>(null);
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' as 'info' | 'success' | 'warning' | 'error'
  });

  // Campos para nova campanha
  const [newCampaign, setNewCampaign] = useState<EmailCampaignData>({
    name: '',
    subject: '',
    content: '',
    target_all: true,
    target_plans: [],
    target_users: [],
    scheduled_at: ''
  });

  // Validação de campos
  const [formErrors, setFormErrors] = useState({
    name: false,
    subject: false,
    content: false
  });

  // Carregar campanhas
  const loadCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllCampaigns();
      setCampaigns(data);
      setFilteredCampaigns(data);
    } catch (err) {
      setError('Erro ao carregar campanhas.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCampaigns();
  }, []);

  // Filtrar campanhas com base no termo de pesquisa
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCampaigns(campaigns);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = campaigns.filter(
        (campaign) =>
          campaign.name.toLowerCase().includes(term) ||
          campaign.subject.toLowerCase().includes(term) ||
          (campaign.status && campaign.status.toLowerCase().includes(term))
      );
      setFilteredCampaigns(filtered);
    }
    setPage(0);
  }, [searchTerm, campaigns]);

  // Manipular abertura/fechamento de diálogo
  const handleOpenDialog = (campaign?: EmailCampaignData) => {
    if (campaign) {
      setSelectedCampaign(campaign);
      setNewCampaign({
        name: campaign.name,
        subject: campaign.subject,
        content: campaign.content,
        target_all: campaign.target_all || false,
        target_plans: campaign.target_plans || [],
        target_users: campaign.target_users || [],
        scheduled_at: campaign.scheduled_at || ''
      });
    } else {
      setSelectedCampaign(null);
      setNewCampaign({
        name: '',
        subject: '',
        content: '',
        target_all: true,
        target_plans: [],
        target_users: [],
        scheduled_at: ''
      });
    }
    setFormErrors({ name: false, subject: false, content: false });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCampaign(null);
  };

  // Validar formulário
  const validateForm = (): boolean => {
    const errors = {
      name: newCampaign.name.trim() === '',
      subject: newCampaign.subject.trim() === '',
      content: newCampaign.content.trim() === ''
    };
    setFormErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  // Criar ou atualizar campanha
  const handleSaveCampaign = async () => {
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: 'Por favor, preencha todos os campos obrigatórios.',
        severity: 'error'
      });
      return;
    }

    try {
      if (selectedCampaign) {
        await updateCampaign(selectedCampaign.id!, newCampaign);
        setSnackbar({
          open: true,
          message: 'Campanha atualizada com sucesso!',
          severity: 'success'
        });
      } else {
        await createCampaign(newCampaign);
        setSnackbar({
          open: true,
          message: 'Campanha criada com sucesso!',
          severity: 'success'
        });
      }
      handleCloseDialog();
      loadCampaigns();
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Erro ao salvar campanha.',
        severity: 'error'
      });
      console.error(err);
    }
  };

  // Deletar campanha
  const handleDeleteCampaign = async (campaignId: string) => {
    if (!window.confirm('Tem certeza que deseja deletar esta campanha?')) {
      return;
    }

    try {
      await deleteCampaign(campaignId);
      setSnackbar({
        open: true,
        message: 'Campanha deletada com sucesso!',
        severity: 'success'
      });
      loadCampaigns();
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Erro ao deletar campanha.',
        severity: 'error'
      });
      console.error(err);
    }
  };

  // Enviar campanha
  const handleSendCampaign = async (campaignId: string) => {
    if (!window.confirm('Tem certeza que deseja enviar esta campanha?')) {
      return;
    }

    try {
      const result = await sendCampaign(campaignId);
      setSnackbar({
        open: true,
        message: result.message,
        severity: 'success'
      });
      loadCampaigns();
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Erro ao enviar campanha.',
        severity: 'error'
      });
      console.error(err);
    }
  };

  // Pré-visualizar campanha
  const handlePreviewCampaign = async (campaignId: string) => {
    try {
      const previewData = await previewCampaign(campaignId);
      setPreview(previewData);
      setOpenPreviewDialog(true);
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Erro ao carregar prévia.',
        severity: 'error'
      });
      console.error(err);
    }
  };

  // Enviar email de teste
  const handleSendTestEmail = async (campaignId: string) => {
    if (!testEmail) {
      setSnackbar({
        open: true,
        message: 'Por favor, insira um email para teste.',
        severity: 'warning'
      });
      return;
    }

    try {
      await sendTestEmail(campaignId, testEmail);
      setSnackbar({
        open: true,
        message: `Email de teste enviado para ${testEmail}`,
        severity: 'success'
      });
      setTestEmail('');
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Erro ao enviar email de teste.',
        severity: 'error'
      });
      console.error(err);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Campanhas de Email
          </Typography>
          <Box>
            <Tooltip title="Atualizar">
              <IconButton onClick={loadCampaigns} sx={{ mr: 1 }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Nova Campanha
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar campanhas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>Assunto</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Destinatários</TableCell>
                      <TableCell>Enviados/Falhas</TableCell>
                      <TableCell>Criado em</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCampaigns
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((campaign) => (
                        <TableRow key={campaign.id}>
                          <TableCell>{campaign.name}</TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                              {campaign.subject}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <CampaignStatus status={campaign.status || 'draft'} />
                          </TableCell>
                          <TableCell>{campaign.total_recipients || 0}</TableCell>
                          <TableCell>
                            {campaign.emails_sent || 0} / {campaign.emails_failed || 0}
                          </TableCell>
                          <TableCell>
                            {campaign.created_at ? formatDateTime(campaign.created_at) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Prévia">
                              <IconButton
                                size="small"
                                onClick={() => handlePreviewCampaign(campaign.id!)}
                              >
                                <PreviewIcon />
                              </IconButton>
                            </Tooltip>
                            {campaign.status !== 'sent' && (
                              <>
                                <Tooltip title="Editar">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenDialog(campaign)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Enviar">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSendCampaign(campaign.id!)}
                                  >
                                    <SendIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Deletar">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteCampaign(campaign.id!)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredCampaigns.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Itens por página:"
              />
            </>
          )}
        </Paper>

        {/* Dialog para criar/editar campanha */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedCampaign ? 'Editar Campanha' : 'Nova Campanha'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nome da Campanha"
                    value={newCampaign.name}
                    onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                    error={formErrors.name}
                    helperText={formErrors.name ? 'Campo obrigatório' : ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Assunto do Email"
                    value={newCampaign.subject}
                    onChange={(e) => setNewCampaign({ ...newCampaign, subject: e.target.value })}
                    error={formErrors.subject}
                    helperText={formErrors.subject ? 'Campo obrigatório' : ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Conteúdo do Email
                  </Typography>
                  <HtmlEmailEditor
                    value={newCampaign.content}
                    onChange={(value) => setNewCampaign({ ...newCampaign, content: value })}
                    error={formErrors.content}
                    helperText={formErrors.content ? 'Campo obrigatório' : ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={newCampaign.target_all}
                        onChange={(e) => setNewCampaign({ ...newCampaign, target_all: e.target.checked })}
                      />
                    }
                    label="Enviar para todos os usuários"
                  />
                </Grid>
                {!newCampaign.target_all && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Planos Específicos</InputLabel>
                      <Select
                        multiple
                        value={newCampaign.target_plans || []}
                        onChange={(e) => setNewCampaign({ ...newCampaign, target_plans: e.target.value as string[] })}
                        label="Planos Específicos"
                      >
                        <MenuItem value="basic">Smart</MenuItem>
                        <MenuItem value="professional">Smart Plus</MenuItem>
                        <MenuItem value="expert">Team</MenuItem>
                        <MenuItem value="unlimited">Enterprise</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Agendar Envio"
                    value={newCampaign.scheduled_at}
                    onChange={(e) => setNewCampaign({ ...newCampaign, scheduled_at: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    helperText="Deixe em branco para enviar imediatamente"
                  />
                </Grid>
                {selectedCampaign && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Email de Teste
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        label="Email para teste"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="seu@email.com"
                      />
                      <Button
                        variant="outlined"
                        startIcon={<EmailIcon />}
                        onClick={() => handleSendTestEmail(selectedCampaign.id!)}
                      >
                        Enviar Teste
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button onClick={handleSaveCampaign} variant="contained" color="primary">
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog para prévia */}
        <Dialog open={openPreviewDialog} onClose={() => setOpenPreviewDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Prévia da Campanha</DialogTitle>
          <DialogContent>
            {preview && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Assunto:
                </Typography>
                <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                  {preview.subject}
                </Typography>
                
                <Typography variant="subtitle2" gutterBottom>
                  Destinatários: {preview.recipient_count}
                </Typography>
                <List dense sx={{ mb: 2 }}>
                  {preview.recipients_preview.map((email, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={email} />
                    </ListItem>
                  ))}
                  {preview.recipient_count > 10 && (
                    <ListItem>
                      <ListItemText primary={`... e mais ${preview.recipient_count - 10} destinatários`} />
                    </ListItem>
                  )}
                </List>

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2" gutterBottom>
                  Conteúdo:
                </Typography>
                <Box
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    p: 2,
                    maxHeight: 400,
                    overflow: 'auto'
                  }}
                  dangerouslySetInnerHTML={{ __html: preview.content }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPreviewDialog(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para mensagens */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
  );
};

export default EmailCampaignPage;

